/**
 * VPN Connectivity Check
 *
 * Verifies the user can reach the API backend AND is authorized as a web
 * client. When ENFORCE_CLIENT_ACCESS is active on the server, the API returns
 * 403 for web clients not on the VPN — even though the server is reachable.
 *
 * A short timeout ensures the guard doesn't hang when the VPN is down.
 */

const VPN_CHECK_TIMEOUT = 5000;

function getCheckUrl() {
    const base = 'https://' + import.meta.env.VITE_API_HOST + ':' + import.meta.env.VITE_API_PORT;

    if (import.meta.env.VITE_VPN_CHECK_URL) {
        return base + '/' + import.meta.env.VITE_VPN_CHECK_URL;
    }

    return base;
}

let lastCheckResult = null;
let lastCheckTime = 0;
const CACHE_TTL = 30000; // cache a successful result for 30s

/**
 * Returns true if the VPN-gated endpoint is reachable and not rejecting
 * the request, false otherwise.
 */
async function isVpnConnected() {
    const now = Date.now();

    if (lastCheckResult === true && (now - lastCheckTime) < CACHE_TTL) {
        return true;
    }

    try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), VPN_CHECK_TIMEOUT);

        const response = await fetch(getCheckUrl(), {
            method: 'GET',
            cache: 'no-store',
            signal: controller.signal,
        });

        clearTimeout(timeout);

        // A 403 means the server is reachable but rejecting web access (no VPN).
        if (response.status === 403) {
            lastCheckResult = false;
            lastCheckTime = now;
            return false;
        }

        lastCheckResult = true;
        lastCheckTime = now;
        return true;
    } catch {
        lastCheckResult = false;
        lastCheckTime = now;
        return false;
    }
}

/**
 * Clears the cached VPN check result, forcing the next call to re-probe.
 */
function resetVpnCheck() {
    lastCheckResult = null;
    lastCheckTime = 0;
}

export { isVpnConnected, resetVpnCheck };
export default { isVpnConnected, resetVpnCheck };
